import { ref, watch, type Ref } from "vue";
import * as Sentry from "@sentry/vue";

import { getDashboardSummary } from "@/api/services/Dashboard";
import { useDashboard } from "@/stores/dashboard/dashboard.store";

// Fast "does this project have any mapped materials?" check for gating the nav (Transport / Waste
// / Reports) on a brand-new project. Reads from the cheap dashboard *summary* endpoint
// (api/dashboard/{id}/summary — emissionBudget/mapping/unit only) instead of the heavy full
// dashboard fetch, and seeds the initial value from a per-project cache so a reload renders the
// correct state on first paint (no "available → dimmed" flip).
//
// Defaults to false: a project we don't yet know about reads as "not mapped", so the nav locks
// immediately — the right bias for new/empty projects. The mapping percentage is structural (the
// share of elements mapped), independent of the selected indicator, so an empty request body is
// enough. On error we keep the seeded value; callers should OR this with the dashboard store's
// live getIsSomethingMapped() — that lifts the lock the moment the first material is mapped and is
// the authoritative fallback if the summary call fails (so an empty project is never wrongly
// unlocked).
const cacheKey = (projectId: string) => `gaiup-${projectId}-has-mapping`;

export function readCachedMapping(projectId: string): boolean | null {
  try {
    const raw = localStorage.getItem(cacheKey(projectId));
    return raw === null ? null : raw === "true";
  } catch {
    return null; // storage unavailable / blocked — ignore
  }
}

function writeCachedMapping(projectId: string, value: boolean) {
  try {
    localStorage.setItem(cacheKey(projectId), String(value));
  } catch {
    // ignore storage errors (incognito / quota)
  }
}

export function useProjectMappingStatus(projectId: Ref<string>) {
  const isMapped = ref(false);
  const dashboard = useDashboard();

  async function refresh(id: string) {
    if (!id) return;
    // Seed from the last-known value so a reload shows the correct state immediately.
    isMapped.value = readCachedMapping(id) ?? false;
    try {
      const summary = await getDashboardSummary(id, {});
      const mapped = (summary?.mapping?.material?.percentage ?? 0) > 0;
      isMapped.value = mapped;
      writeCachedMapping(id, mapped);
    } catch (e) {
      // Keep the seeded value; the live store watch below is the authoritative fallback.
      Sentry.captureException(e);
    }
  }

  watch(projectId, (id) => refresh(id), { immediate: true });

  // Mirror the live store: the moment mapping is confirmed (e.g. the user maps their first
  // material), unlock locally AND persist it, so the route guard — which reads only the cache —
  // never wrongly redirects after mapping. Runs in component context, so touching the store here
  // is safe (unlike inside a router guard).
  watch(
    () => dashboard.getIsSomethingMapped(),
    (mapped) => {
      if (mapped && projectId.value) {
        isMapped.value = true;
        writeCachedMapping(projectId.value, true);
      }
    },
    { immediate: true }
  );

  return { isMapped };
}
